import { Box, Text } from '@timelyapp/react';
import classNames from 'classnames';
import type { ChangeEvent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './Textarea.module.scss';

interface Props
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'children' | 'onChange'> {
  onChange?: (value: string) => void;
  /** If true, this textarea will automatically grow and shrink with its contents. */
  autoGrow?: boolean;
  /** Limits number of characters the user can enter in the textarea */
  limit?: number;
  children?: string | null;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, Props>(
  ({ autoGrow, limit, className, children, onChange, value, ...rest }, ref) => {
    const [text, setText] = useState(truncateToLimit(children || String(value || ''), limit));

    // Sync internal state when value prop changes from parent
    useEffect(() => {
      const newValue = truncateToLimit(children || String(value || ''), limit);
      setText(newValue);
    }, [value, children, limit]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        let updatedText = event.target.value;
        if (limit && updatedText.length > limit) {
          updatedText = truncateToLimit(updatedText, limit);
        }

        setText(updatedText);
        onChange?.(updatedText);
      },
      [limit, onChange]
    );

    const containerClass = classNames(styles.container, className);
    let element = (
      <textarea
        {...rest}
        ref={ref}
        onChange={handleChange}
        className={containerClass}
        value={text}
      />
    );

    if (autoGrow) {
      const wrapperClass = classNames(styles.autoGrow, containerClass);
      element = (
        <div className={wrapperClass} data-replicated-value={text}>
          {element}
        </div>
      );
    }

    if (limit && text.length >= limit * 0.5) {
      element = (
        <Box.Column gap="small">
          {element}
          <Text size="small" className={styles.count}>
            {text.length} / {limit}
          </Text>
        </Box.Column>
      );
    }

    return element;
  }
);

Textarea.displayName = 'Textarea';

const truncateToLimit = (str: string, limit: number | undefined) => {
  if (!limit || str?.length <= limit) {
    return str;
  }

  return str.substring(0, limit);
};

export default Textarea;
